import { useCallback, useEffect, useRef, useState } from "react";

export interface PortalMenuRect {
  /** Distance from viewport top — set when menu opens below the trigger. */
  top?: number;
  /** Distance from viewport bottom — set when menu opens above the trigger. */
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

export interface UsePortalMenuOptions {
  open: boolean;
  onOutsideClick?: () => void;
  /** Recompute when one shared menu moves between different triggers. */
  anchorKey?: unknown;
  /**
   * Extra portal-rendered elements to also treat as "inside" this menu —
   * e.g. a nested dropdown (like `MultiSelectSearch`'s own item list) that
   * portals to `document.body` as a sibling rather than a DOM descendant of
   * this menu, so a plain `menuRef.contains()` check would miss clicks in it.
   */
  containedRefs?: React.RefObject<HTMLElement>[];
  /** Distance from the trigger to the menu. Defaults to `MENU_GAP`. */
  gap?: number;
  /**
   * `"bottom"` (default): opens below the trigger, matches its width, flips
   * above when the viewport is cramped — the standard dropdown contract.
   * `"right"`: opens beside the trigger, top-aligned with it, flips to the
   * trigger's left when the viewport is cramped — for a Notion-style nested
   * submenu (e.g. the filter chip's operator flyout). Requires `menuWidth`.
   * `"left"`: the mirror of `"right"` — opens beside the trigger on its left,
   * flips right when cramped, and shifts up so a `menuHeight`-tall card stays
   * inside the viewport — for hover cards next to a right-docked panel.
   */
  placement?: PortalMenuPlacement;
  /** Estimated menu width, used to decide whether side placements must flip. */
  menuWidth?: number;
  /**
   * Estimated menu height. `"left"`: keeps the card on screen near the bottom edge. `"bottom"`: when the menu
   * would not fit below, it opens above if there is more room there, and its height is capped to the room.
   */
  menuHeight?: number;
  /** Horizontal offset added to the trigger's left edge, `"bottom"` placement only. Shifts the menu right (positive) or left (negative) without changing the anchor/flip logic. */
  offsetX?: number;
  /** Tallest the menu may grow before it scrolls (`"bottom"`/`"right"`). Defaults to 260px; raise it for menus with a header field. */
  maxHeight?: number;
}

export interface UsePortalMenu<T extends HTMLElement, M extends HTMLElement> {
  /** Writable: a menu shared by several triggers points it at the one that was clicked. */
  triggerRef: React.MutableRefObject<T | null>;
  menuRef: React.RefObject<M>;
  rect: PortalMenuRect | null;
}

export type PortalMenuPlacement = "bottom" | "right" | "left";

const MENU_GAP = 4;
const VIEWPORT_PADDING = 8;
const PREFERRED_MAX_HEIGHT = 260;
const MIN_BELOW_SPACE_BEFORE_FLIP = 120;

export interface ComputePortalMenuRectInput {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
}

/** Pure positioning logic — used by the hook and unit tests. */
export const computePortalMenuRect = (
  trigger: ComputePortalMenuRectInput,
  viewportHeight: number,
  gap: number = MENU_GAP,
  placement: PortalMenuPlacement = "bottom",
  menuWidth = 0,
  viewportWidth: number = typeof window !== "undefined" ? window.innerWidth : 0,
  offsetX = 0,
  menuHeight = 0,
  preferredMaxHeight: number = PREFERRED_MAX_HEIGHT,
): PortalMenuRect => {
  if (placement === "left") {
    const triggerRight = trigger.left + trigger.width;
    const spaceLeft = trigger.left - gap - VIEWPORT_PADDING;
    const spaceRight = viewportWidth - triggerRight - gap - VIEWPORT_PADDING;
    const openRightSide = spaceLeft < menuWidth && spaceRight >= menuWidth;
    const lowestTop = viewportHeight - menuHeight - VIEWPORT_PADDING;
    const top = Math.max(VIEWPORT_PADDING, Math.min(trigger.top, lowestTop));
    return {
      top,
      left: openRightSide
        ? triggerRight + gap
        : Math.max(VIEWPORT_PADDING, trigger.left - menuWidth - gap),
      width: menuWidth,
      maxHeight: Math.max(viewportHeight - top - VIEWPORT_PADDING, 0),
    };
  }

  if (placement === "right") {
    const triggerRight = trigger.left + trigger.width;
    const spaceRight = viewportWidth - triggerRight - gap - VIEWPORT_PADDING;
    const spaceLeft = trigger.left - gap - VIEWPORT_PADDING;
    const openLeftSide = spaceRight < menuWidth && spaceLeft >= menuWidth;
    return {
      top: trigger.top,
      left: openLeftSide ? trigger.left - menuWidth - gap : triggerRight + gap,
      width: menuWidth,
      maxHeight: Math.min(
        preferredMaxHeight,
        Math.max(viewportHeight - trigger.top - VIEWPORT_PADDING, 0),
      ),
    };
  }

  const spaceBelow = viewportHeight - trigger.bottom - gap - VIEWPORT_PADDING;
  const spaceAbove = trigger.top - gap - VIEWPORT_PADDING;
  const openAbove =
    menuHeight > 0
      ? spaceBelow < menuHeight && spaceAbove > spaceBelow
      : spaceBelow < MIN_BELOW_SPACE_BEFORE_FLIP && spaceAbove > 0;
  const heightCap = menuHeight > 0 ? menuHeight : preferredMaxHeight;

  if (openAbove) {
    const maxHeight = Math.min(heightCap, Math.max(spaceAbove, 0));
    return {
      bottom: viewportHeight - trigger.top + gap,
      left: trigger.left + offsetX,
      width: trigger.width,
      maxHeight,
    };
  }

  return {
    top: trigger.bottom + gap,
    left: trigger.left + offsetX,
    width: trigger.width,
    maxHeight: Math.min(heightCap, Math.max(spaceBelow, 0)),
  };
};

/**
 * Anchors a portal-rendered menu to a trigger element using fixed positioning.
 * Recomputes on scroll/resize while open; flips above the trigger when there
 * is insufficient viewport space below; fires onOutsideClick on outside click.
 */
export const usePortalMenu = <
  T extends HTMLElement = HTMLButtonElement,
  M extends HTMLElement = HTMLDivElement,
>({
  open,
  onOutsideClick,
  anchorKey,
  containedRefs,
  gap,
  placement,
  menuWidth,
  menuHeight,
  offsetX,
  maxHeight,
}: UsePortalMenuOptions): UsePortalMenu<T, M> => {
  const triggerRef = useRef<T | null>(null);
  const menuRef = useRef<M>(null);
  const [rect, setRect] = useState<PortalMenuRect | null>(null);

  const compute = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setRect(
      computePortalMenuRect(
        r,
        window.innerHeight,
        gap,
        placement,
        menuWidth,
        undefined,
        offsetX,
        menuHeight,
        maxHeight,
      ),
    );
  }, [gap, placement, menuWidth, menuHeight, offsetX, maxHeight]);

  useEffect(() => {
    if (!open) return;
    compute();
    // Re-run after layout when trigger moves (e.g. add row mounts below tile grid).
    const raf = requestAnimationFrame(() => compute());
    const handle = () => compute();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [open, compute, anchorKey]);

  useEffect(() => {
    if (!open || !onOutsideClick) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      if (containedRefs?.some((ref) => ref.current?.contains(target))) return;
      onOutsideClick();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onOutsideClick, containedRefs]);

  return { triggerRef, menuRef, rect };
};
